import { FILTER_TYPES } from "../filters/deviceFilters";
import {
  getDeviceChargingFilterValue,
  getDeviceInKioskFilterValue,
  isDeviceInKiosk,
  readDeviceIpFromPayload,
  readDeviceIsPlugged,
  readDevicePayloadField,
} from "./mdmDeviceDisplayUtils";

export const createSortDefinitions = ({
  normalizeTimestampMs,
  cleanSsid,
  getDuplicateFolderPath,
  getDeviceCurrentFolderName,
  getStatus,
}) => ({
  deviceName: {
    type: "string",
    getValue: (device) => device.deviceId,
  },
  agentCheckIn: {
    type: "number",
    getValue: (device) => normalizeTimestampMs(device?.deviceInfo?.timestamp),
  },
  battery: {
    type: "number",
    getValue: (device) => {
      const value = Number(device?.deviceInfo?.batteryLevel);
      return Number.isFinite(value) ? value : null;
    },
  },
  chargingStatus: {
    type: "number",
    getValue: (device) => {
      const isPlugged = readDeviceIsPlugged(device);
      if (isPlugged === true) return 1;
      if (isPlugged === false) return 0;
      return null;
    },
  },
  networkSsid: {
    type: "string",
    getValue: (device) => cleanSsid(device?.deviceInfo?.SSID),
  },
  hasIp: {
    type: "number",
    getValue: (device) => (readDeviceIpFromPayload(device?.rawPayload) ? 1 : 0),
  },
  inKiosk: {
    type: "number",
    getValue: (device) => (isDeviceInKiosk(device) ? 1 : 0),
  },
  folderPath: {
    type: "string",
    getValue: (device) => getDuplicateFolderPath(device?.deviceId),
  },
  currentFolder: {
    type: "string",
    getValue: (device) => getDeviceCurrentFolderName(device?.deviceId),
  },
  status: {
    type: "string",
    getValue: (device) => getStatus(device?.isConnected).label,
  },
  notes: {
    type: "string",
    getValue: (device) => String(readDevicePayloadField(device, ["notes"]) ?? "").toLowerCase(),
  },
});

const payloadBoolSortValue = (device, keys) => {
  const v = readDevicePayloadField(device, keys);
  if (v === true) return 1;
  if (v === false) return 0;
  return null;
};

export const createOptionalPayloadCacheColumnSortDefinitions = ({ normalizeTimestampMs }) => ({
  app_in_idle: {
    type: "number",
    getValue: (device) => payloadBoolSortValue(device, ["app_in_idle"]),
  },
  wall_id: {
    type: "string",
    getValue: (device) => String(readDevicePayloadField(device, ["wall_id"]) ?? "").toLowerCase(),
  },
  device_in_cradle: {
    type: "number",
    getValue: (device) => payloadBoolSortValue(device, ["device_in_cradle"]),
  },
  is_scan_and_go_ready: {
    type: "number",
    getValue: (device) => payloadBoolSortValue(device, ["is_scan_and_go_ready"]),
  },
  allowed: {
    type: "number",
    getValue: (device) => payloadBoolSortValue(device, ["allowed"]),
  },
  "S&G_battery_level": {
    type: "number",
    getValue: (device) => {
      const n = Number(readDevicePayloadField(device, ["S&G_battery_level"]));
      return Number.isFinite(n) ? n : null;
    },
  },
  "S&G_updated": {
    type: "number",
    getValue: (device) => normalizeTimestampMs(readDevicePayloadField(device, ["S&G_updated"])),
  },
  "S&G_last_release_ts": {
    type: "number",
    getValue: (device) => normalizeTimestampMs(readDevicePayloadField(device, ["S&G_last_release_ts"])),
  },
  locationLogVersionUpdated: {
    type: "number",
    getValue: (device) =>
      normalizeTimestampMs(
        readDevicePayloadField(device, ["location_log"])?.updated_at ||
        readDevicePayloadField(device, ["location_log"])?.updatedAt
      ),
  },
});

export const createFullFilterDefinitions = ({ normalizeTimestampMs, getDeviceCurrentFolderFilterValue }) => [
  {
    id: "searchText",
    type: FILTER_TYPES.SEARCH,
    getValue: (device) => device.deviceId,
  },
  {
    id: "agentCheckInDate",
    type: FILTER_TYPES.DATE_RANGE,
    getValue: (device) => normalizeTimestampMs(device?.deviceInfo?.timestamp),
  },
  {
    id: "batteryLevel",
    type: FILTER_TYPES.RANGE,
    getValue: (device) => {
      const battery = Number(device?.deviceInfo?.batteryLevel);
      return Number.isFinite(battery) ? battery : null;
    },
  },
  {
    id: "status",
    type: FILTER_TYPES.DROPDOWN,
    getValue: (device) => (device?.isConnected ? "connected" : "disconnected"),
  },
  {
    id: "hasIp",
    type: FILTER_TYPES.DROPDOWN,
    getValue: (device) => (readDeviceIpFromPayload(device?.rawPayload) ? "true" : "false"),
  },
  {
    id: "chargingStatus",
    type: FILTER_TYPES.DROPDOWN,
    getValue: (device) => getDeviceChargingFilterValue(device),
  },
  {
    id: "inKiosk",
    type: FILTER_TYPES.DROPDOWN,
    getValue: (device) => getDeviceInKioskFilterValue(device),
  },
  {
    id: "currentFolder",
    type: FILTER_TYPES.DROPDOWN,
    getValue: (device) => getDeviceCurrentFolderFilterValue(device?.deviceId),
  },
];
